/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AppState } from './app-state.service';
import {
	materialize,
	switchMap,
	dematerialize,
	filter,
	catchError,
	map,
	tap,
	finalize,
} from 'rxjs/operators';
import {
	from,
	MonoTypeOperatorFunction,
	of,
	NEVER,
	concat,
	Observable,
	throwError,
	ObservableNotification,
} from 'rxjs';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { FBDB_REQUEST_DISABLED } from '@touchplan/strings';
import * as moment from 'moment';
import firebase from 'firebase/compat/app';

import {
	IFbdbRequest,
	IFbdbRequestResult,
	FbdbRequestConfig,
	RequestType,
	DefaultProjectCopyConfig,
	ProjectCopyConfig,
	IProjectCopyResult,
	PlanCopyConfig,
	IPlanCopyResult,
	IBiembedUrlResult,
	IBiembedUrlConfig,
	IBiIndependentEmbedUrlConfig,
	IntercomInviteNewUserConfig,
	IntercomInviteNewUserResult,
	IntercomInviteExistingUserConfig,
	ICsvPlanConfig,
	ICsvProjectConfig,
	IFbdbRequestPlanProjectResults,
	IProjectReportsConfig,
	IProjectReportsFbdbRequestResults,
	IBundleHeading,
	IPPCReport,
	IAnyReportTypes,
	IExportTicketsConfig,
	IPlanNearlineRequestResults,
	ITakeSnapshotResult,
	ITakeSnapshotConfig,
} from './fbdb-requests.interfaces';
import { ApplicationWideMessageService } from '../common/application-wide-message/application-wide-message.module';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { Role } from '@touchplan/common';
import {
	timeoutLocalized,
	TimeoutLocalizedError,
} from '../plain-javascript/services/rxjs-internal-extensions';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

export * from './fbdb-requests.interfaces';

export class FbdbRequestError extends Error {}
export const baseRequestPath = 'fbdbRequests';
export const resultSubPath = 'results';
export const cancelSubPath = 'isCancelled';

//TODO
// - there can only be a single subscription per request unsure if this can or even should be modified
// (could do some kind of multicast/ refCount voodoo in theory)
// - come up with presence solution
// - implement rest of requests
// - analytics

//javadocs
// https://bitbucket.org/mocasystems/mb4-fbdb-multi/src/develop/fbdb/src/main/java/com/mocasystems/mb4/fbdb/request/RequestServicerConstants.java
// standard https://bitbucket.org/mocasystems/mb4-fbdb-multi/src/76dc6501afde9a3657a8eb30bc53609be2259ba6/fbdb/src/main/java/com/mocasystems/mb4/fbdb/request/PlanMoveDatesServicer.java#lines-44
// reports https://bitbucket.org/mocasystems/mb4-fbdb-multi/src/76dc6501afde9a3657a8eb30bc53609be2259ba6/fbdb/src/main/java/com/mocasystems/mb4/fbdb/request/ProjectReportParams.java#lines-33

/**
 * ## Using a request
 * ### As an observable
 * Preferred api, allows for progress updates, most integratable into angular.
 *
 * #### Care about progress updates (rare)
 * ```
 * this.projectCopyRequest(config).subscribe({
 * 	next: (val) => { }, //do progress update stuff
 * 	complete: () => { }, //do all done stuff
 * 	error: (err) => { } //handle timeouts or other fbdb related errors
 * });
 * ```
 *
 * #### Final result
 * ```
 * this.projectCopyRequest(config).pipe(last()).subscribe({
 * next: (val) => { }, //the final value
 * error: (err) => { } //handle timeouts or other fbdb related errors
 * });
 * ```
 *
 * ### As a promise
 * Use for easier backwards compatibility with angularjs
 * ```
 * this.projectCopyRequest(config).toPromise()
 * .then(val => { }) //final result
 * .catch(err => { }); //error
 * ```
 *
 * ### Cancelling a request
 * Cancelling a request has been baked into the observable lifecycle. So all that's necessary to cancel a request, including writing
 * the "cancel" key to firebase on the supported requests is to unsubscribe. In theory any mean of stopping the listener
 * should work, currently only a formal unsubscribe has been tested though. This dives _slightly_ into undocumented rxjs behavior.
 *
 * ```
 * const unsub = this.projectCopyRequest(config).subscribe(() => { });
 * unsub.unsubscribe();
 * ```
 *
 * ## Creating a request
 * - define an appropriate config interface in ./fbdb-requests.interfaces.ts
 * - implement a method on this service which accepts that config as a parameter, calls and returns request()
 */

@Injectable({
	providedIn: 'root',
})
export class FbdbRequests {
	private statusMessageDisplayed: boolean;
	private logger: ILoggerService;

	constructor(
		private af: AngularFireDatabase,
		private appState: AppState,
		private applicationWideMessageService: ApplicationWideMessageService
	) {
		this.logger = LoggerServiceFactory.create('FbdbRequests');
		this.applicationWideMessageService.getAppWideMessage().subscribe((message) => {
			this.statusMessageDisplayed = !!(message?.message || message?.popupMessage);
			window.statusMessageDisplayed = this.statusMessageDisplayed;
		});
	}

	private getRequestRef(key: string) {
		const fbdbRequestPath = fancyFirebaseGetters.getFbdbRequestsPath(key);
		return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(fbdbRequestPath);
	}

	// there's nothing in the angularfire api to do this, so fallback to direct firebase
	public getNewRequestId() {
		const fbdbRequestsPath = fancyFirebaseGetters.getFbdbRequestsPath();
		return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(fbdbRequestsPath).push().key;
	}

	/** return true if valid, clean in place */
	private cleanParams(params: any) {
		//just use the old one...
		for (const key in params) {
			if (params[key] === undefined) {
				this.logger.log('undefined found on key: ', key);
				delete params[key];
			}
		}
	}

	private buildRequest(requestName: RequestType, params?: any): IFbdbRequest {
		return {
			name: requestName as string,
			params,
			requestingUserId: this.appState.userSync ? this.appState.userSync.$id : null,
		};
	}

	private deleteRequest(requestRef: firebase.database.Reference, config?: FbdbRequestConfig) {
		if (config && config.disableDeletion) {
			return;
		}
		requestRef.remove();
	}

	private cancelRequest(requestRef: firebase.database.Reference) {
		const cancelRef = requestRef.child(cancelSubPath);
		cancelRef.set(true);
	}

	// private detectCompletionOperator(obs)

	public request<ResultOverride extends IFbdbRequestResult>(
		requestName: RequestType,
		params: any = {},
		config?: FbdbRequestConfig
	) {
		//early abort
		if (this.statusMessageDisplayed) {
			throwError(new FbdbRequestError(FBDB_REQUEST_DISABLED));
			return;
		}

		const id = config?.overrideRequestId ?? this.getNewRequestId();
		const requestPath = this.getRequestRef(id);
		const resultPath = requestPath.child(resultSubPath);

		const result$ = this.af.object<ResultOverride>(resultPath).valueChanges();

		this.cleanParams(params);
		const requestData = this.buildRequest(requestName, params);

		let successfullyCompleted = false;
		let errored = false;
		let cancelled = false;

		const startTime = moment();
		const endTime = () => moment().diff(startTime, 'seconds');
		analyticsEventsService.fbdbRequestStarted(requestName, id, new Date());
		this.logger.debug(requestData);

		requestPath.set(requestData);

		return result$.pipe(
			//timeout after 30s of no emissions including started at
			switchMap((result) => {
				if (successfullyCompleted) {
					this.logger.error('Multiple subscriptions to the same request is not supported, see TP-4467');
				}
				if (!result || !result.startedAt) {
					return concat(
						of(result),
						NEVER.pipe(
							timeoutLocalized(
								config?.timeoutOverride && config.timeoutOverride >= 30000 ? config.timeoutOverride : 30000
							)
						)
					);
				}
				return of(result);
			}),
			filter((result) => !!result), //doesn't really "need" not yet loaded values in the output
			//if this is hit the observable has been completed by something _other_ than a successful request. This should be
			//interpreted as a cancel. This works solely because the server completion interpretation pipe hasn't run yet.
			completionSideEffect(() => {
				cancelled = true;
			}),
			convertCompletion(),
			map((result) => {
				result.requestId = id;
				// if (config && config.includeRequest) { return { result, originalRequest: requestData }; } //uncomment and test if necessary
				return result;
			}),
			completionSideEffect(() => {
				if (successfullyCompleted) {
					this.logger.error('Multiple subscriptions to the same request is not supported, see TP-4467');
				}
				successfullyCompleted = true;
				this.deleteRequest(requestPath, config);
				analyticsEventsService.fbdbRequestCompleted(requestName, id, new Date(), endTime());
			}),
			catchError((e: Error) => {
				if (e instanceof TimeoutLocalizedError) {
					this.cancelRequest(requestPath);
					analyticsEventsService.fbdbRequestCancelled(requestName, id, new Date(), endTime());
				}
				errored = true;
				analyticsEventsService.fbdbRequestHitError(requestName, id, new Date(), endTime(), e.message);
				throw e;
			}),
			finalize(() => {
				//didn't complete, and didn't error, so it must have unsubbed
				//alternatively, the observable has been completed by another observable. Which is considered a cancel
				if ((!successfullyCompleted && !errored) || cancelled) {
					this.cancelRequest(requestPath);
					analyticsEventsService.fbdbRequestCancelled(requestName, id, new Date(), endTime());
				}
			})
		);
	}

	public projectCopyRequest(config: ProjectCopyConfig) {
		return this.request<IProjectCopyResult>(RequestType.projectCopy, config);
	}

	public planCopyRequest(config: PlanCopyConfig) {
		return this.request<IPlanCopyResult>(RequestType.planCopy, config);
	}

	public takeSnapshot(
		config: ITakeSnapshotConfig,
		fbdbConfig?: FbdbRequestConfig
	): Observable<ITakeSnapshotResult> {
		return this.request<ITakeSnapshotResult>(RequestType.takeSnapshot, config, fbdbConfig);
	}

	public biembedUrlRequest(config: IBiembedUrlConfig): Observable<IBiembedUrlResult> {
		return this.request<IBiembedUrlResult>(RequestType.biembedUrl, config);
	}

	public biIndependentEmbedUrlRequest(config: IBiIndependentEmbedUrlConfig): Observable<IBiembedUrlResult> {
		return this.request<IBiembedUrlResult>(RequestType.biembedUrl, config);
	}

	public intercomInviteNewUser(config: IntercomInviteNewUserConfig): Observable<IntercomInviteNewUserResult> {
		return this.request<IntercomInviteNewUserResult>(RequestType.intercomInviteNewUser, config);
	}
	public intercomInviteExistingUser(
		config: IntercomInviteExistingUserConfig
	): Observable<IFbdbRequestResult> {
		return this.request<IFbdbRequestResult>(RequestType.intercomInviteExistingUser, config);
	}

	// Shouldn’t be called directly and instead be called via the cloud storage service.
	public requestPlanTable(
		config: ICsvPlanConfig,
		fbdbConfig?: FbdbRequestConfig
	): Observable<IFbdbRequestPlanProjectResults> {
		return this.request<IFbdbRequestPlanProjectResults>(RequestType.planTables, config, fbdbConfig);
	}

	//  Shouldn’t be called directly and instead be called via the cloud storage service.
	public requestProjectTable(
		config: ICsvProjectConfig,
		fbdbConfig?: FbdbRequestConfig
	): Observable<IFbdbRequestPlanProjectResults> {
		return this.request<IFbdbRequestPlanProjectResults>(RequestType.projectTables, config, fbdbConfig);
	}

	//  Shouldn’t be called directly and instead be called via the cloud storage service.
	public requestExportTickets(
		config: IExportTicketsConfig,
		fbdbRequestId: string
	): Observable<IFbdbRequestPlanProjectResults> {
		const fbdbRequestConfig: FbdbRequestConfig = { overrideRequestId: fbdbRequestId };
		return this.request<IFbdbRequestPlanProjectResults>(RequestType.ticketExport, config, fbdbRequestConfig);
	}

	public getStatusMessageDisplayed(): boolean {
		return this.statusMessageDisplayed;
	}

	// explanation for reports request code can be further documented here:
	// https://mocasystems.atlassian.net/wiki/spaces/DI/pages/2448424961/Fbdb+Request+Service

	/**
	 *  method to request PPC update
	 * @param  projectId projectId to be passed in as an extra param
	 */
	public requestPPCUpdate(projectId: string): Observable<IFbdbRequestPlanProjectResults> {
		return this.request<IFbdbRequestPlanProjectResults>(RequestType.projectTables, { projectId });
	}

	/**
	 * method to request single report
	 * @param  reportsList  list of reports to fetch
	 */
	public requestReports(
		reportParams: IAnyReportTypes,
		config?: FbdbRequestConfig
	): Observable<IProjectReportsFbdbRequestResults> {
		this.logger.log('requestReports: reportParams', reportParams);
		return this.request<IProjectReportsFbdbRequestResults>(
			RequestType.projectReport,
			reportParams,
			config
		).pipe(
			tap((resWithoutName) => (resWithoutName.reportName = reportParams.reportName)) //all them side effects
		);
	}

	/**
	 * add params such as reportTitle and roleIds to reportList
	 * @param  reportsList  list of reports to fetch
	 * @param  roles  list of role objects
	 * @param params additional params to be passed into report for request
	 * @param reportName name of report
	 */
	public addParamsToBreakUpByRoleProjectReportList(
		selectedRoleIds: string[] | null,
		roles: Map<string, Role>,
		params: IAnyReportTypes,
		reportTitle: string
	) {
		//handle undefined/ null/ empty selections (could be more performant)
		if (!selectedRoleIds || !selectedRoleIds.length) {
			selectedRoleIds = Array.from(roles.keys());
		}

		const reportList = selectedRoleIds
			.filter((roleId) => {
				const role = roles.get(roleId);
				if (!role) {
					this.logger.log(`selection ${roleId} does not exist, skipping...`);
				}
				return role;
			}) //quietly exclude roleId selection's that don't exist
			.map((roleId) => {
				const roleName = roles.get(roleId)?.view.roleName;
				return {
					...params,
					roleIds: [roleId],
					reportTitle: `${roleName} ${reportTitle}`,
				};
			});
		return reportList;
	}

	/**
	 * wrap each report in appropriate header, footer and bundleInterstitial between each
	 * @param  reportsList  list of reports to fetch
	 */
	public wrapProjectReports(
		reportsList: IProjectReportsConfig[],
		projectId: string
	): IPPCReport[] | IBundleHeading[] {
		const bundleHeader = { projectId: projectId, reportName: 'bundleHeader' };
		const bundleFooter = { projectId: projectId, reportName: 'bundleFooter' };
		const bundleInterstitial = { projectId: projectId, reportName: 'bundleInterstitial' };
		let wrappedReports = [];

		wrappedReports = reportsList.reduce((preWrappedReport, report, idx) => {
			// If it's the last report, don't add bundleInterstitial
			if (idx === reportsList.length - 1) {
				return [...preWrappedReport, report];
			}
			return [...preWrappedReport, report, bundleInterstitial];
		}, []);

		return [bundleHeader, ...wrappedReports, bundleFooter];
	}

	/**
	 * requests each report bundle via this.request to fbdb
	 * @param  reportsList  list of reports to fetch
	 */
	public requestProjectReportBundle(
		reportsList: IProjectReportsConfig[],
		projectId: string,
		config?: FbdbRequestConfig
	) {
		this.logger.log('requestProjectReportBundle: reportsList', reportsList);
		return this.request<IProjectReportsFbdbRequestResults>(
			RequestType.projectReportBundle,
			{
				reports: this.wrapProjectReports(reportsList, projectId),
			},
			config
		).pipe(
			tap((resWithoutName) => (resWithoutName.reportName = RequestType.projectReportBundle)) //all them side effects
		);
	}

	/**
	 * Requests to set the archived plans to the nearline backend status
	 * @param planIds list of archived plans
	 * @param projectId the project id
	 * @param config additional FbdbRequestConfig
	 */
	public requestPlanNearline(
		planIds: string[],
		projectId: string,
		config?: FbdbRequestConfig
	): Observable<IPlanNearlineRequestResults> {
		this.logger.log('requestPlanNearline: planIds', planIds);
		return this.request<IPlanNearlineRequestResults>(
			RequestType.planNearline,
			{ projectId, planIds },
			config
		);
	}
}

/** Detects the completedAt key existing in the output and internally trigger the observable to complete. */
function convertCompletion<
	RequestOverride extends IFbdbRequestResult
>(): MonoTypeOperatorFunction<RequestOverride> {
	return function convertCompletionImplementation(source) {
		return source.pipe(
			materialize(),
			switchMap((result) => {
				const outputList = [result as ObservableNotification<RequestOverride>];
				if (result.value && result.value.completedAt) {
					outputList.push({ kind: 'C' });
					// outputList.push(Notification.createComplete());
				} else if (result.value && result.value.errorMessage) {
					//error does not forward the next first
					return of(
						// Notification.createError<RequestOverride>(new FbdbRequestError(result.value.errorMessage))
						{ kind: 'E', error: new FbdbRequestError(result.value.errorMessage) }
					);
				}
				return from(outputList);
			}),
			dematerialize()
		);
	};
}

/** Detects the completedAt key existing in the output and internally trigger the observable to complete. */
function completionSideEffect<T>(cb: () => void): MonoTypeOperatorFunction<T> {
	return function convertCompletionImplementation(source) {
		return source.pipe(
			materialize(),
			tap((notification) => {
				if (notification.kind === 'C') {
					cb();
				}
			}),
			dematerialize()
		);
	};
}
